import { useEffect, useState } from 'react'

import { FxSpinner } from '@/components/fx'
import { apiClient, documentsApi } from '@/lib/api'
import { createLimiter } from '@/lib/limit'
import { cn } from '@/lib/utils'

/**
 * The cropped pixels of one field — the thing the reviewer actually decides
 * on. The recognizer's text is a claim; this is the evidence.
 *
 * The route is behind the bearer token, so an `<img src>` pointed at it comes
 * back 401 with no useful error: the browser's own image request carries no
 * Authorization header. The bytes are fetched with the session and wrapped in
 * an object URL instead.
 *
 * **The crops queue.** The Sammelprüfung renders one row per flagged field —
 * a page of 25 documents with a dozen flagged fields each mounts hundreds of
 * these at once, and each one is an authenticated blob fetch. Fired together
 * they fill the browser's per-host connection pool and the review's own
 * writes queue up behind them, so they go through a shared gate of four,
 * the same one the overview's per-card counts use.
 *
 * **The object URL is revoked on unmount and on every change.** A review
 * session walks hundreds of fields, and a blob left behind per field is a
 * tab that grows all afternoon — the browser cannot collect one while a URL
 * for it exists, however dead the image element is.
 */

/** Four at a time, as on the overview. */
const snippets = createLimiter(4)

export interface SnippetOptions {
  /** Which stored page the crop is taken from. `enhanced` is the contrast-
   *  corrected page the analysis itself read. */
  source?: 'enhanced' | 'analyzed' | 'original'
  /** Pixels of the page kept around the box, so the field's printed label
   *  and the line under the handwriting stay visible. */
  pad?: number
}

/** The snippet's object URL, its loading state, and its failure. Exported
 *  because a row that draws its own frame wants the URL, not the element. */
export function useSnippetUrl(
  templateId: string | undefined,
  documentId: string | undefined,
  fieldId: string | undefined,
  { source = 'enhanced', pad = 8 }: SnippetOptions = {},
) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [authVersion, setAuthVersion] = useState(0)

  // A token refresh mid-session must not leave every crop on screen broken.
  useEffect(() => {
    const unsubscribe = apiClient.onAuthVersionChange(setAuthVersion)
    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (!templateId || !documentId || !fieldId) {
      setUrl(null)
      return
    }
    const controller = new AbortController()
    let objectUrl: string | null = null
    setError(null)
    // Stop rendering the previous crop before this effect's cleanup revokes
    // it, or switching fields paints a revoked URL — a broken-image flash.
    setUrl(null)

    void snippets(() =>
      // A row scrolled past (or a document left) before its turn came never
      // needs its bytes: the gate must not hold a slot for a fetch nobody is
      // waiting for.
      controller.signal.aborted
        ? Promise.reject(new DOMException('Aborted', 'AbortError'))
        : documentsApi.snippetObjectUrl(templateId, documentId, fieldId, {
            source,
            pad,
            signal: controller.signal,
          }),
    )
      .then((next) => {
        if (controller.signal.aborted) {
          // The field was switched while the bytes were in flight; nothing
          // will render this one, so it has to be released here.
          URL.revokeObjectURL(next)
          return
        }
        objectUrl = next
        setUrl(next)
      })
      .catch((cause: Error) => {
        if (!controller.signal.aborted) setError(cause)
      })

    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [templateId, documentId, fieldId, source, pad, authVersion])

  return { url, error, loading: url === null && error === null }
}

export interface FieldSnippetProps extends SnippetOptions {
  templateId: string
  documentId: string
  fieldId: string
  /** The field's name, which is what the crop is *of*. Used for the image's
   *  accessible name — "Ausschnitt: Nachname". */
  label: string
  className?: string
  /** Applied to the image itself, for the 64px row crop vs. the big panel. */
  imgClassName?: string
}

/** Shown in place of the crop when there is none: a document analysed before
 *  snippets existed, a field the worker could not crop, an expired session.
 *  Never a broken-image icon — the reviewer has to know whether they are
 *  looking at nothing or at an empty field. */
function Fallback({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-sm bg-fill px-4 text-small text-text-muted',
        className,
      )}
    >
      Kein Ausschnitt verfügbar
    </div>
  )
}

export function FieldSnippet({
  templateId,
  documentId,
  fieldId,
  label,
  source,
  pad,
  className,
  imgClassName,
}: FieldSnippetProps) {
  const { url, error } = useSnippetUrl(templateId, documentId, fieldId, { source, pad })

  if (error) return <Fallback className={className} />
  if (!url) {
    return (
      <div className={cn('flex items-center justify-center rounded-sm bg-fill', className)}>
        <FxSpinner label="Ausschnitt wird geladen …" />
      </div>
    )
  }
  return (
    <div className={cn('flex items-center justify-center overflow-hidden rounded-sm bg-surface', className)}>
      <img src={url} alt={`Ausschnitt: ${label}`} className={cn('max-h-full max-w-full object-contain', imgClassName)} />
    </div>
  )
}
